/**
 * Importable/Inputs.tsx
 *
 * Reusable form inputs shared across multiple pages.
 * Add new reusable inputs here as the project grows.
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { Autocomplete, Box, Button, IconButton, Paper, TextField, Tooltip, Typography } from "@mui/material";
import TableChartIcon from "@mui/icons-material/TableChart";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import StrikethroughSIcon from "@mui/icons-material/StrikethroughS";
import FormatColorTextIcon from "@mui/icons-material/FormatColorText";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import { ColorPicker } from "./Pickers";
import TIMEZONES, { formatTimezoneLabel, formatTimezoneLabelForDate, getTimezoneSearchTerms } from "@/utils/timezones";
import { looksLikeTsv, tsvToHtmlTable, attachTableInteractions } from "@/utils/tsvTable";

// ---------------------------------------------------------------------------
// Currency data
// ---------------------------------------------------------------------------
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Comprehensive list of world currencies.
 * Searchable by: code (USD), symbol ($), or any word in the name (United, States, Dollar).
 */
export const CURRENCIES = [{
  code: "AED",
  symbol: "د.إ",
  name: "UAE Dirham"
}, {
  code: "AFN",
  symbol: "؋",
  name: "Afghan Afghani"
}, {
  code: "ALL",
  symbol: "L",
  name: "Albanian Lek"
}, {
  code: "AMD",
  symbol: "֏",
  name: "Armenian Dram"
}, {
  code: "ANG",
  symbol: "ƒ",
  name: "Netherlands Antillean Guilder"
}, {
  code: "AOA",
  symbol: "Kz",
  name: "Angolan Kwanza"
}, {
  code: "ARS",
  symbol: "$",
  name: "Argentine Peso"
}, {
  code: "AUD",
  symbol: "$",
  name: "Australian Dollar"
}, {
  code: "AWG",
  symbol: "ƒ",
  name: "Aruban Florin"
}, {
  code: "AZN",
  symbol: "₼",
  name: "Azerbaijani Manat"
}, {
  code: "BAM",
  symbol: "KM",
  name: "Bosnia-Herzegovina Convertible Mark"
}, {
  code: "BBD",
  symbol: "$",
  name: "Barbadian Dollar"
}, {
  code: "BDT",
  symbol: "৳",
  name: "Bangladeshi Taka"
}, {
  code: "BGN",
  symbol: "лв",
  name: "Bulgarian Lev"
}, {
  code: "BHD",
  symbol: "BD",
  name: "Bahraini Dinar"
}, {
  code: "BIF",
  symbol: "Fr",
  name: "Burundian Franc"
}, {
  code: "BMD",
  symbol: "$",
  name: "Bermudian Dollar"
}, {
  code: "BND",
  symbol: "$",
  name: "Brunei Dollar"
}, {
  code: "BOB",
  symbol: "Bs.",
  name: "Bolivian Boliviano"
}, {
  code: "BRL",
  symbol: "R$",
  name: "Brazilian Real"
}, {
  code: "BSD",
  symbol: "$",
  name: "Bahamian Dollar"
}, {
  code: "BTN",
  symbol: "Nu",
  name: "Bhutanese Ngultrum"
}, {
  code: "BWP",
  symbol: "P",
  name: "Botswana Pula"
}, {
  code: "BYN",
  symbol: "Br",
  name: "Belarusian Ruble"
}, {
  code: "BZD",
  symbol: "$",
  name: "Belize Dollar"
}, {
  code: "CAD",
  symbol: "$",
  name: "Canadian Dollar"
}, {
  code: "CDF",
  symbol: "Fr",
  name: "Congolese Franc"
}, {
  code: "CHF",
  symbol: "Fr",
  name: "Swiss Franc"
}, {
  code: "CLP",
  symbol: "$",
  name: "Chilean Peso"
}, {
  code: "CNY",
  symbol: "¥",
  name: "Chinese Yuan"
}, {
  code: "COP",
  symbol: "$",
  name: "Colombian Peso"
}, {
  code: "CRC",
  symbol: "₡",
  name: "Costa Rican Colón"
}, {
  code: "CUP",
  symbol: "$",
  name: "Cuban Peso"
}, {
  code: "CVE",
  symbol: "$",
  name: "Cape Verdean Escudo"
}, {
  code: "CZK",
  symbol: "Kč",
  name: "Czech Koruna"
}, {
  code: "DJF",
  symbol: "Fr",
  name: "Djiboutian Franc"
}, {
  code: "DKK",
  symbol: "kr",
  name: "Danish Krone"
}, {
  code: "DOP",
  symbol: "$",
  name: "Dominican Peso"
}, {
  code: "DZD",
  symbol: "دج",
  name: "Algerian Dinar"
}, {
  code: "EGP",
  symbol: "£",
  name: "Egyptian Pound"
}, {
  code: "ERN",
  symbol: "Nfk",
  name: "Eritrean Nakfa"
}, {
  code: "ETB",
  symbol: "Br",
  name: "Ethiopian Birr"
}, {
  code: "EUR",
  symbol: "€",
  name: "Euro"
}, {
  code: "FJD",
  symbol: "$",
  name: "Fijian Dollar"
}, {
  code: "FKP",
  symbol: "£",
  name: "Falkland Islands Pound"
}, {
  code: "GBP",
  symbol: "£",
  name: "British Pound Sterling"
}, {
  code: "GEL",
  symbol: "₾",
  name: "Georgian Lari"
}, {
  code: "GHS",
  symbol: "₵",
  name: "Ghanaian Cedi"
}, {
  code: "GIP",
  symbol: "£",
  name: "Gibraltar Pound"
}, {
  code: "GMD",
  symbol: "D",
  name: "Gambian Dalasi"
}, {
  code: "GNF",
  symbol: "Fr",
  name: "Guinean Franc"
}, {
  code: "GTQ",
  symbol: "Q",
  name: "Guatemalan Quetzal"
}, {
  code: "GYD",
  symbol: "$",
  name: "Guyanese Dollar"
}, {
  code: "HKD",
  symbol: "$",
  name: "Hong Kong Dollar"
}, {
  code: "HNL",
  symbol: "L",
  name: "Honduran Lempira"
}, {
  code: "HRK",
  symbol: "kn",
  name: "Croatian Kuna"
}, {
  code: "HTG",
  symbol: "G",
  name: "Haitian Gourde"
}, {
  code: "HUF",
  symbol: "Ft",
  name: "Hungarian Forint"
}, {
  code: "IDR",
  symbol: "Rp",
  name: "Indonesian Rupiah"
}, {
  code: "ILS",
  symbol: "₪",
  name: "Israeli New Shekel"
}, {
  code: "INR",
  symbol: "₹",
  name: "Indian Rupee"
}, {
  code: "IQD",
  symbol: "ع.د",
  name: "Iraqi Dinar"
}, {
  code: "IRR",
  symbol: "﷼",
  name: "Iranian Rial"
}, {
  code: "ISK",
  symbol: "kr",
  name: "Icelandic Króna"
}, {
  code: "JMD",
  symbol: "$",
  name: "Jamaican Dollar"
}, {
  code: "JOD",
  symbol: "JD",
  name: "Jordanian Dinar"
}, {
  code: "JPY",
  symbol: "¥",
  name: "Japanese Yen"
}, {
  code: "KES",
  symbol: "Ksh",
  name: "Kenyan Shilling"
}, {
  code: "KGS",
  symbol: "с",
  name: "Kyrgystani Som"
}, {
  code: "KHR",
  symbol: "៛",
  name: "Cambodian Riel"
}, {
  code: "KMF",
  symbol: "Fr",
  name: "Comorian Franc"
}, {
  code: "KPW",
  symbol: "₩",
  name: "North Korean Won"
}, {
  code: "KRW",
  symbol: "₩",
  name: "South Korean Won"
}, {
  code: "KWD",
  symbol: "KD",
  name: "Kuwaiti Dinar"
}, {
  code: "KYD",
  symbol: "$",
  name: "Cayman Islands Dollar"
}, {
  code: "KZT",
  symbol: "₸",
  name: "Kazakhstani Tenge"
}, {
  code: "LAK",
  symbol: "₭",
  name: "Laotian Kip"
}, {
  code: "LBP",
  symbol: "ل.ل",
  name: "Lebanese Pound"
}, {
  code: "LKR",
  symbol: "₨",
  name: "Sri Lankan Rupee"
}, {
  code: "LRD",
  symbol: "$",
  name: "Liberian Dollar"
}, {
  code: "LSL",
  symbol: "L",
  name: "Lesotho Loti"
}, {
  code: "LYD",
  symbol: "LD",
  name: "Libyan Dinar"
}, {
  code: "MAD",
  symbol: "MAD",
  name: "Moroccan Dirham"
}, {
  code: "MDL",
  symbol: "L",
  name: "Moldovan Leu"
}, {
  code: "MGA",
  symbol: "Ar",
  name: "Malagasy Ariary"
}, {
  code: "MKD",
  symbol: "ден",
  name: "Macedonian Denar"
}, {
  code: "MMK",
  symbol: "K",
  name: "Myanmar Kyat"
}, {
  code: "MNT",
  symbol: "₮",
  name: "Mongolian Tögrög"
}, {
  code: "MOP",
  symbol: "P",
  name: "Macanese Pataca"
}, {
  code: "MRU",
  symbol: "UM",
  name: "Mauritanian Ouguiya"
}, {
  code: "MUR",
  symbol: "₨",
  name: "Mauritian Rupee"
}, {
  code: "MVR",
  symbol: "Rf",
  name: "Maldivian Rufiyaa"
}, {
  code: "MWK",
  symbol: "MK",
  name: "Malawian Kwacha"
}, {
  code: "MXN",
  symbol: "$",
  name: "Mexican Peso"
}, {
  code: "MYR",
  symbol: "RM",
  name: "Malaysian Ringgit"
}, {
  code: "MZN",
  symbol: "MT",
  name: "Mozambican Metical"
}, {
  code: "NAD",
  symbol: "$",
  name: "Namibian Dollar"
}, {
  code: "NGN",
  symbol: "₦",
  name: "Nigerian Naira"
}, {
  code: "NIO",
  symbol: "C$",
  name: "Nicaraguan Córdoba"
}, {
  code: "NOK",
  symbol: "kr",
  name: "Norwegian Krone"
}, {
  code: "NPR",
  symbol: "₨",
  name: "Nepalese Rupee"
}, {
  code: "NZD",
  symbol: "$",
  name: "New Zealand Dollar"
}, {
  code: "OMR",
  symbol: "﷼",
  name: "Omani Rial"
}, {
  code: "PAB",
  symbol: "B/.",
  name: "Panamanian Balboa"
}, {
  code: "PEN",
  symbol: "S/.",
  name: "Peruvian Sol"
}, {
  code: "PGK",
  symbol: "K",
  name: "Papua New Guinean Kina"
}, {
  code: "PHP",
  symbol: "₱",
  name: "Philippine Peso"
}, {
  code: "PKR",
  symbol: "₨",
  name: "Pakistani Rupee"
}, {
  code: "PLN",
  symbol: "zł",
  name: "Polish Złoty"
}, {
  code: "PYG",
  symbol: "₲",
  name: "Paraguayan Guaraní"
}, {
  code: "QAR",
  symbol: "﷼",
  name: "Qatari Riyal"
}, {
  code: "RON",
  symbol: "lei",
  name: "Romanian Leu"
}, {
  code: "RSD",
  symbol: "din",
  name: "Serbian Dinar"
}, {
  code: "RUB",
  symbol: "₽",
  name: "Russian Ruble"
}, {
  code: "RWF",
  symbol: "Fr",
  name: "Rwandan Franc"
}, {
  code: "SAR",
  symbol: "﷼",
  name: "Saudi Riyal"
}, {
  code: "SBD",
  symbol: "$",
  name: "Solomon Islands Dollar"
}, {
  code: "SCR",
  symbol: "₨",
  name: "Seychellois Rupee"
}, {
  code: "SDG",
  symbol: "£",
  name: "Sudanese Pound"
}, {
  code: "SEK",
  symbol: "kr",
  name: "Swedish Krona"
}, {
  code: "SGD",
  symbol: "$",
  name: "Singapore Dollar"
}, {
  code: "SHP",
  symbol: "£",
  name: "Saint Helena Pound"
}, {
  code: "SLL",
  symbol: "Le",
  name: "Sierra Leonean Leone"
}, {
  code: "SOS",
  symbol: "Sh",
  name: "Somali Shilling"
}, {
  code: "SRD",
  symbol: "$",
  name: "Surinamese Dollar"
}, {
  code: "STN",
  symbol: "Db",
  name: "São Tomé and Príncipe Dobra"
}, {
  code: "SVC",
  symbol: "₡",
  name: "Salvadoran Colón"
}, {
  code: "SYP",
  symbol: "£",
  name: "Syrian Pound"
}, {
  code: "SZL",
  symbol: "L",
  name: "Swazi Lilangeni"
}, {
  code: "THB",
  symbol: "฿",
  name: "Thai Baht"
}, {
  code: "TJS",
  symbol: "SM",
  name: "Tajikistani Somoni"
}, {
  code: "TMT",
  symbol: "T",
  name: "Turkmenistani Manat"
}, {
  code: "TND",
  symbol: "DT",
  name: "Tunisian Dinar"
}, {
  code: "TOP",
  symbol: "T$",
  name: "Tongan Paʻanga"
}, {
  code: "TRY",
  symbol: "₺",
  name: "Turkish Lira"
}, {
  code: "TTD",
  symbol: "$",
  name: "Trinidad and Tobago Dollar"
}, {
  code: "TWD",
  symbol: "$",
  name: "New Taiwan Dollar"
}, {
  code: "TZS",
  symbol: "Sh",
  name: "Tanzanian Shilling"
}, {
  code: "UAH",
  symbol: "₴",
  name: "Ukrainian Hryvnia"
}, {
  code: "UGX",
  symbol: "Sh",
  name: "Ugandan Shilling"
}, {
  code: "USD",
  symbol: "$",
  name: "United States Dollar"
}, {
  code: "UYU",
  symbol: "$",
  name: "Uruguayan Peso"
}, {
  code: "UZS",
  symbol: "soʻm",
  name: "Uzbekistani Som"
}, {
  code: "VES",
  symbol: "Bs.S",
  name: "Venezuelan Bolívar Soberano"
}, {
  code: "VND",
  symbol: "₫",
  name: "Vietnamese Đồng"
}, {
  code: "VUV",
  symbol: "Vt",
  name: "Vanuatu Vatu"
}, {
  code: "WST",
  symbol: "T",
  name: "Samoan Tālā"
}, {
  code: "XAF",
  symbol: "Fr",
  name: "Central African CFA Franc"
}, {
  code: "XCD",
  symbol: "$",
  name: "East Caribbean Dollar"
}, {
  code: "XOF",
  symbol: "Fr",
  name: "West African CFA Franc"
}, {
  code: "XPF",
  symbol: "Fr",
  name: "CFP Franc"
}, {
  code: "YER",
  symbol: "﷼",
  name: "Yemeni Rial"
}, {
  code: "ZAR",
  symbol: "R",
  name: "South African Rand"
}, {
  code: "ZMW",
  symbol: "ZK",
  name: "Zambian Kwacha"
}, {
  code: "ZWL",
  symbol: "$",
  name: "Zimbabwean Dollar"
}];

// ---------------------------------------------------------------------------
// Top-10 most-traded currencies (shown in compact mode)
// ---------------------------------------------------------------------------

/**
 * The 10 most-traded world currencies by daily FX volume.
 * Ordered by trading volume descending.
 */
export const COMPACT_CURRENCIES = [{
  code: "USD",
  symbol: "$",
  name: "United States Dollar"
},
// ~89%
{
  code: "EUR",
  symbol: "€",
  name: "Euro"
},
// ~29%
{
  code: "JPY",
  symbol: "¥",
  name: "Japanese Yen"
},
// ~17%
{
  code: "GBP",
  symbol: "£",
  name: "British Pound Sterling"
},
// ~10%
{
  code: "CNY",
  symbol: "¥",
  name: "Chinese Yuan"
},
// ~8.5%
{
  code: "CHF",
  symbol: "Fr",
  name: "Swiss Franc"
},
// ~6.4%
{
  code: "AUD",
  symbol: "$",
  name: "Australian Dollar"
},
// ~6.1%
{
  code: "CAD",
  symbol: "$",
  name: "Canadian Dollar"
},
// ~5.8%
{
  code: "HKD",
  symbol: "$",
  name: "Hong Kong Dollar"
},
// ~3.8%
{
  code: "SGD",
  symbol: "$",
  name: "Singapore Dollar"
} // ~2.4%
];

// ---------------------------------------------------------------------------
// CurrencySelect
// ---------------------------------------------------------------------------

/**
 * Searchable currency selector.
 *
 * The search field accepts:
 *  - ISO code     ("USD", "cad")
 *  - Symbol       ("$", "€", "£")
 *  - Full name    ("United States Dollar")
 *  - Any word(s) from the full name ("Canada", "dollar", "swiss")
 *
 * Selected value is the ISO 4217 code (e.g. "USD").
 * The displayed option format is:  USD — $ — United States Dollar
 */
export function CurrencySelect({
  value,
  onChange,
  label = "Currency",
  required = false,
  error = false,
  helperText,
  fullWidth = true,
  size = "medium",
  disabled = false,
  compact = true
}) {
  const [inputValue, setInputValue] = useState("");
  // Tracks whether the user expanded the list within this open session.
  const [expandedLocally, setExpandedLocally] = useState(false);

  // True when the full list should be shown (either prop or user toggled).
  const isExpanded = !compact || expandedLocally;
  const selected = useMemo(() => CURRENCIES.find(c => c.code === value) ?? null, [value]);

  /**
   * Filter currencies by input text.
   * Always searches the full CURRENCIES list so that typing "Nepal" in
   * compact mode still finds NPR even though it isn't in the short list.
   * When the query is empty, compact mode returns only COMPACT_CURRENCIES
   * (unless expanded locally or compact=false).
   */
  const filterOptions = (_options, {
    inputValue: q
  }) => {
    if (!q) return isExpanded ? CURRENCIES : COMPACT_CURRENCIES;
    const lower = q.toLowerCase().trim();
    const words = lower.split(/\s+/);
    return CURRENCIES.filter(c => {
      const code = c.code.toLowerCase();
      const sym = c.symbol.toLowerCase();
      const name = c.name.toLowerCase();
      // Exact or prefix match on code or symbol
      if (code.startsWith(lower) || sym === lower) return true;
      // Every typed word must appear somewhere in code, symbol, or name
      return words.every(w => code.includes(w) || sym.includes(w) || name.includes(w));
    });
  };

  // The base options list drives what's shown before the user types.
  // filterOptions will override this, but Autocomplete needs a non-empty
  // options array that contains the current value so it doesn't clear it.
  const baseOptions = isExpanded ? CURRENCIES : COMPACT_CURRENCIES;
  return /*#__PURE__*/_jsx(Autocomplete, {
    options: baseOptions,
    onClose: () => {
      // Reset to compact when dropdown closes, but keep the value.
      if (compact) setExpandedLocally(false);
    },
    value: selected,
    inputValue: inputValue,
    onInputChange: (_e, v) => setInputValue(v),
    onChange: (_e, option) => {
      onChange(option?.code ?? "");
      if (!option) setInputValue("");
    },
    filterOptions: filterOptions,
    getOptionLabel: opt => opt.code,
    isOptionEqualToValue: (opt, val) => opt.code === val.code,
    disabled: disabled,
    fullWidth: fullWidth,
    PaperComponent: paperProps => /*#__PURE__*/_jsxs(Paper, {
      ...paperProps,
      children: [paperProps.children, compact && !isExpanded && /*#__PURE__*/_jsx(Box, {
        sx: {
          px: 2,
          py: 0.75,
          borderTop: "1px solid",
          borderColor: "divider"
        },
        children: /*#__PURE__*/_jsx(Button, {
          size: "small",
          variant: "text",
          onMouseDown: e => {
            // Prevent closing the dropdown on click
            e.preventDefault();
            setExpandedLocally(true);
          },
          sx: {
            textTransform: "none",
            fontSize: "0.8rem"
          },
          children: "Show extended list"
        })
      })]
    }),
    renderInput: params => /*#__PURE__*/_jsx(TextField, {
      ...params,
      label: label,
      required: required,
      error: error,
      helperText: helperText,
      size: size,
      placeholder: "USD, $, Dollar\u2026"
    }),
    renderOption: (props, option) => {
      const {
        key,
        ...rest
      } = props;
      return /*#__PURE__*/_jsx(Tooltip, {
        title: option.name,
        placement: "right",
        arrow: true,
        children: /*#__PURE__*/_jsxs(Box, {
          component: "li",
          ...rest,
          children: [/*#__PURE__*/_jsx(Typography, {
            component: "span",
            variant: "body2",
            sx: {
              fontWeight: 600,
              minWidth: 28,
              mr: 0.75
            },
            children: option.symbol
          }), /*#__PURE__*/_jsx(Typography, {
            component: "span",
            variant: "body2",
            color: "text.secondary",
            sx: {
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap"
            },
            children: option.name
          })]
        })
      }, key);
    }
  });
}

// ---------------------------------------------------------------------------
// TimezoneSelect
// ---------------------------------------------------------------------------

/**
 * Searchable IANA timezone selector.
 *
 * Accepts searches by city name, abbreviation (PST, AEST), offset description,
 * or informal terms (e.g. "California", "Pacific Standard").
 *
 * Selected value is an IANA timezone identifier (e.g. "America/Los_Angeles").
 */
export function TimezoneSelect({
  value,
  onChange,
  label = "Timezone",
  required = false,
  error = false,
  helperText,
  fullWidth = true,
  size = "medium",
  disabled = false,
  referenceDate
}) {
  const getLabel = tz => referenceDate ? formatTimezoneLabelForDate(tz, referenceDate) : formatTimezoneLabel(tz);
  return /*#__PURE__*/_jsx(Autocomplete, {
    options: TIMEZONES,
    value: value || null,
    getOptionLabel: getLabel,
    filterOptions: (options, {
      inputValue: q
    }) => {
      const input = q.trim().toLowerCase();
      if (!input) return options;
      return options.filter(tz => getTimezoneSearchTerms(tz).some(term => term.toLowerCase().includes(input)));
    },
    onChange: (_e, v) => onChange(v ?? ""),
    disabled: disabled,
    fullWidth: fullWidth,
    renderInput: params => /*#__PURE__*/_jsx(TextField, {
      ...params,
      label: label,
      required: required,
      error: error,
      helperText: helperText,
      size: size
    })
  });
}

// ---------------------------------------------------------------------------
// PasteTableInput
// ---------------------------------------------------------------------------

/**
 * A self-contained "Insert Table" widget.
 *
 * Renders a textarea that accepts TSV data pasted from Google Sheets / Excel.
 * When the user clicks "Insert Table", a live preview of the interactive
 * pasted-table is shown. The `onInsert(html)` callback fires with the raw
 * HTML string so the parent can inject it into a contenteditable editor.
 *
 * The live preview mounts `attachTableInteractions` so resizing, column
 * reordering, header-cell coloring and row/column additions all work in
 * the preview before the table is inserted into the document.
 */

export function PasteTableInput({
  onInsert,
  onCancel
}) {
  const [tsv, setTsv] = useState("");
  const [previewHtml, setPreviewHtml] = useState(null);
  const [error, setError] = useState(null);
  const previewRef = useRef(null);
  const cleanupRef = useRef(null);

  // Parse TSV from the textarea or from raw HTML clipboard data.
  // Returns the pasted-table HTML string, or null when the data isn't
  // recognised as a table.
  const buildTableHtml = raw => {
    if (looksLikeTsv(raw)) return tsvToHtmlTable(raw);
    return null;
  };

  // When the preview div is mounted/updated, attach interactive behaviors.
  const attachPreview = el => {
    // Tear down any previous attachment.
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    previewRef.current = el;
    if (el) {
      cleanupRef.current = attachTableInteractions(el);
    }
  };
  const handleGenerate = () => {
    setError(null);
    const html = buildTableHtml(tsv.trim());
    if (!html) {
      setError("No table detected. Paste tab-separated data (e.g. copied from Google Sheets or Excel).");
      setPreviewHtml(null);
      return;
    }
    setPreviewHtml(html);
  };
  const handleInsert = () => {
    // Grab the current live HTML from the preview div so any in-preview
    // edits (resizing, reordering, cell edits) are captured.
    const liveEl = previewRef.current?.querySelector(".pasted-table-wrap");
    const html = liveEl ? liveEl.outerHTML : previewHtml ?? "";
    if (!html) return;
    // Clean up interactive listeners before handing off.
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    onInsert(html);
    setTsv("");
    setPreviewHtml(null);
  };
  const handleCancel = () => {
    if (cleanupRef.current) {
      cleanupRef.current();
      cleanupRef.current = null;
    }
    setTsv("");
    setPreviewHtml(null);
    setError(null);
    onCancel?.();
  };
  return /*#__PURE__*/_jsxs(Box, {
    sx: {
      display: "flex",
      flexDirection: "column",
      gap: 1.5
    },
    children: [/*#__PURE__*/_jsx(Typography, {
      variant: "caption",
      color: "text.secondary",
      sx: {
        lineHeight: 1.4
      },
      children: "Copy cells from Google Sheets, Excel, or any spreadsheet, then paste them into the box below."
    }), /*#__PURE__*/_jsx(TextField, {
      multiline: true,
      minRows: 4,
      maxRows: 10,
      placeholder: "Paste spreadsheet data here…",
      value: tsv,
      onChange: e => {
        setTsv(e.target.value);
        setError(null);
      },
      onPaste: e => {
        // Try to intercept HTML clipboard from Sheets/Excel which
        // provides a <table> in text/html even when the plain-text
        // payload is empty or already TSV.
        const plain = e.clipboardData.getData("text/plain");
        // If plain text has tabs, let the default paste fill the
        // textarea so the user can see what was pasted.
        if (plain.includes("\t")) return;
        // Otherwise check for an HTML table in the clipboard.
        const html = e.clipboardData.getData("text/html");
        if (html && /<table/i.test(html)) {
          // Convert HTML table rows to TSV so our parser can
          // handle it. We use a temporary DOM element.
          e.preventDefault();
          const tmp = document.createElement("div");
          tmp.innerHTML = html;
          const rows = Array.from(tmp.querySelectorAll("tr"));
          const tsvLines = rows.map(tr => Array.from(tr.querySelectorAll("td, th")).map(c => c.innerText.replace(/\t/g, " ")).join("\t"));
          setTsv(tsvLines.join("\n"));
          setError(null);
        }
      },
      error: !!error,
      helperText: error,
      size: "small",
      fullWidth: true,
      inputProps: {
        style: {
          fontFamily: "monospace",
          fontSize: 12
        }
      }
    }), /*#__PURE__*/_jsxs(Box, {
      sx: {
        display: "flex",
        gap: 1
      },
      children: [/*#__PURE__*/_jsx(Button, {
        variant: "contained",
        size: "small",
        startIcon: /*#__PURE__*/_jsx(TableChartIcon, {}),
        onClick: handleGenerate,
        disabled: !tsv.trim(),
        sx: {
          flex: 1
        },
        children: "Preview Table"
      }), onCancel && /*#__PURE__*/_jsx(Button, {
        variant: "text",
        size: "small",
        color: "inherit",
        onClick: handleCancel,
        children: "Cancel"
      })]
    }), previewHtml && /*#__PURE__*/_jsxs(Box, {
      sx: {
        mt: 0.5
      },
      children: [/*#__PURE__*/_jsx(Typography, {
        variant: "caption",
        color: "text.secondary",
        sx: {
          mb: 0.5,
          display: "block"
        },
        children: "Preview \u2014 resize columns, reorder, click header to color cells:"
      }), /*#__PURE__*/_jsx(Box, {
        ref: attachPreview,
        contentEditable: true,
        suppressContentEditableWarning: true,
        onInput: () => {
          /* suppress warning */
        },
        sx: {
          overflowX: "auto",
          pt: "36px",
          // room for the floating toolbar
          pb: 1,
          px: 1,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 1,
          bgcolor: "background.paper",
          minHeight: 80,
          outline: "none",
          "& .pasted-table-wrap": {
            position: "relative"
          }
        },
        dangerouslySetInnerHTML: {
          __html: previewHtml
        }
      }), /*#__PURE__*/_jsx(Button, {
        variant: "contained",
        color: "success",
        size: "small",
        fullWidth: true,
        sx: {
          mt: 1
        },
        onClick: handleInsert,
        children: "Insert Table into Document"
      })]
    })]
  });
}

// ---------------------------------------------------------------------------
// InlineTableInserter
// ---------------------------------------------------------------------------

/**
 * A floating panel that appears anchored to a sentinel DOM element inside the
 * contenteditable editor. The sentinel is a placeholder `<div>` inserted at
 * the user's caret when they click "Insert Table" in the toolbar.
 *
 * When the user confirms, `onInsert(html, sentinelEl)` fires — the caller
 * should replace `sentinelEl` with the provided table HTML. When the user
 * cancels, `onCancel(sentinelEl)` fires so the caller can remove the sentinel.
 */

export function InlineTableInserter({
  sentinelEl,
  onInsert,
  onCancel
}) {
  const [error, setError] = useState(null);
  const tryConvert = raw => {
    if (!raw || !raw.trim()) return false;
    if (!looksLikeTsv(raw)) return false;
    const html = tsvToHtmlTable(raw);
    if (!html || !sentinelEl) return false;
    onInsert(html, sentinelEl);
    return true;
  };
  if (!sentinelEl) return null;
  const panel = /*#__PURE__*/_jsxs(Box, {
    sx: {
      my: 2,
      p: 2,
      border: "2px dashed",
      borderColor: "primary.main",
      borderRadius: 2,
      bgcolor: "action.hover",
      display: "flex",
      flexDirection: "column",
      gap: 1.25
    },
    children: [/*#__PURE__*/_jsxs(Box, {
      sx: {
        display: "flex",
        alignItems: "center",
        gap: 1
      },
      children: [/*#__PURE__*/_jsx(TableChartIcon, {
        color: "primary",
        fontSize: "small"
      }), /*#__PURE__*/_jsx(Typography, {
        variant: "subtitle2",
        sx: {
          fontWeight: 700,
          flex: 1
        },
        children: "Paste spreadsheet data to insert a table"
      }), /*#__PURE__*/_jsx(Button, {
        size: "small",
        variant: "text",
        color: "inherit",
        onClick: () => onCancel(sentinelEl),
        children: "Cancel"
      })]
    }), /*#__PURE__*/_jsx(Typography, {
      variant: "caption",
      color: "text.secondary",
      sx: {
        lineHeight: 1.4
      },
      children: "Copy cells from Google Sheets, Excel, or any spreadsheet, then paste them in the box below. The table will be inserted automatically."
    }), /*#__PURE__*/_jsx(TextField, {
      autoFocus: true,
      multiline: true,
      minRows: 3,
      maxRows: 8,
      placeholder: "Paste spreadsheet data here\u2026",
      onChange: e => {
        // If the user types/pastes manually and it looks like TSV, convert.
        if (tryConvert(e.target.value)) return;
        setError(null);
      },
      onPaste: e => {
        // Prefer plain-text TSV.
        const plain = e.clipboardData.getData("text/plain");
        if (plain && plain.includes("\t")) {
          e.preventDefault();
          if (!tryConvert(plain)) {
            setError("No table detected in pasted data.");
          }
          return;
        }
        // Fall back to HTML clipboard with a <table> (some apps).
        const html = e.clipboardData.getData("text/html");
        if (html && /<table/i.test(html)) {
          e.preventDefault();
          const tmp = document.createElement("div");
          tmp.innerHTML = html;
          const rows = Array.from(tmp.querySelectorAll("tr"));
          const tsvLines = rows.map(tr => Array.from(tr.querySelectorAll("td, th")).map(c => c.innerText.replace(/\t/g, " ")).join("\t"));
          const tsvText = tsvLines.join("\n");
          if (!tryConvert(tsvText)) {
            setError("No table detected in pasted data.");
          }
          return;
        }
        // Plain text without tabs: let it fill the textarea so the
        // user can see what they pasted, then surface the error.
        setError("Pasted data doesn't look like a table. Copy cells from a spreadsheet (e.g. Google Sheets) and paste here.");
      },
      error: !!error,
      helperText: error,
      size: "small",
      fullWidth: true,
      inputProps: {
        style: {
          fontFamily: "monospace",
          fontSize: 12
        }
      }
    })]
  });
  if (typeof document === "undefined") return null;
  // Portal directly into the sentinel so the panel renders inline at the
  // user's cursor position within the editor (not as a floating overlay).
  return /*#__PURE__*/ReactDOM.createPortal(panel, sentinelEl);
}

// ═══════════════════════════════════════════════════════════════════════════
// StylableInput — contenteditable rich-text input with format toolbar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Multi-line rich-text input with bold/italic/underline/strikethrough,
 * text color and highlight controls. Stores HTML; the consumer is
 * responsible for rendering it (e.g. via dangerouslySetInnerHTML on the
 * published page).
 */
export function StylableInput({
  value,
  onChange,
  label,
  placeholder = "Enter text...",
  minHeight = "6rem",
  id
}) {
  const editorRef = useRef(null);
  // Guard so the external-sync effect skips changes that originated here
  const internalRef = useRef(false);
  const [focused, setFocused] = useState(false);
  // True when a non-collapsed selection lives inside this editor
  const [selectionActive, setSelectionActive] = useState(false);
  const [textColor, setTextColor] = useState("#000000");
  const [highlight, setHighlight] = useState("#ffff00");
  const savedRangeRef = useRef(null);
  const textColorBtnRef = useRef(null);
  const highlightBtnRef = useRef(null);
  const [textColorPickerOpen, setTextColorPickerOpen] = useState(false);
  const [highlightPickerOpen, setHighlightPickerOpen] = useState(false);
  const tooltipFallback = label ? `Please select text within ${label} to use` : "Please select text to use";

  /** Apply a simple inline formatting command. */
  const applyInline = cmd => {
    const el = editorRef.current;
    if (!el) return;
    if (!el.contains(document.activeElement)) el.focus();
    try {
      document.execCommand(cmd);
    } catch {}
    internalRef.current = true;
    onChange(el.innerHTML);
  };

  /** Remove all inline formatting from the selection (or full editor if nothing selected). */
  const resetFormatting = () => {
    const el = editorRef.current;
    if (!el) return;
    if (!el.contains(document.activeElement)) el.focus();
    const sel = window.getSelection();
    const hasSelection = !!(sel && sel.rangeCount > 0 && !sel.isCollapsed);
    if (!hasSelection) return;
    try {
      document.execCommand("removeFormat");
    } catch {}
    internalRef.current = true;
    onChange(el.innerHTML);
  };

  /** Wrap the current selection in a colored span. */
  const applyColor = (prop, color, savedRange) => {
    const el = editorRef.current;
    if (!el) return;
    let range = savedRange ?? null;
    if (!range) {
      const sel = window.getSelection();
      if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
        const r = sel.getRangeAt(0);
        let node = r.commonAncestorContainer;
        while (node) {
          if (node === el) {
            range = r;
            break;
          }
          node = node.parentNode;
        }
      }
    }
    if (!range || range.collapsed) return;
    if (!el.contains(document.activeElement)) el.focus();
    const sel = window.getSelection();
    if (sel && savedRange) {
      sel.removeAllRanges();
      sel.addRange(savedRange);
    }
    try {
      document.execCommand("styleWithCSS", false, "true");
      if (prop === "color") {
        document.execCommand("foreColor", false, color);
      } else {
        document.execCommand("hiliteColor", false, color);
      }
    } catch {}
    internalRef.current = true;
    onChange(el.innerHTML);
  };

  // Track whether the current selection lives inside *this* editor instance
  useEffect(() => {
    const handler = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setSelectionActive(false);
        return;
      }
      const range = sel.getRangeAt(0);
      let node = range.commonAncestorContainer;
      while (node) {
        if (node === editorRef.current) {
          setSelectionActive(true);
          return;
        }
        node = node.parentNode;
      }
      setSelectionActive(false);
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, []);

  // Sync externally-driven value changes (prefill, edit-load) into the div
  useEffect(() => {
    if (internalRef.current) {
      internalRef.current = false;
      return;
    }
    if (editorRef.current) {
      const newVal = value || "";
      if (editorRef.current.innerHTML !== newVal) {
        editorRef.current.innerHTML = newVal;
      }
    }
  }, [value]);
  const inlineButtons = [{
    label: "Bold",
    cmd: "bold",
    icon: /*#__PURE__*/_jsx(FormatBoldIcon, {
      fontSize: "small"
    })
  }, {
    label: "Italic",
    cmd: "italic",
    icon: /*#__PURE__*/_jsx(FormatItalicIcon, {
      fontSize: "small"
    })
  }, {
    label: "Underline",
    cmd: "underline",
    icon: /*#__PURE__*/_jsx(FormatUnderlinedIcon, {
      fontSize: "small"
    })
  }, {
    label: "Strikethrough",
    cmd: "strikeThrough",
    icon: /*#__PURE__*/_jsx(StrikethroughSIcon, {
      fontSize: "small"
    })
  }];
  return /*#__PURE__*/_jsxs(Box, {
    children: [/*#__PURE__*/_jsxs(Box, {
      sx: {
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        flexWrap: "wrap",
        pb: 0.5
      },
      children: [label && /*#__PURE__*/_jsxs(Typography, {
        variant: "caption",
        sx: {
          color: "text.secondary",
          mr: 0.75,
          userSelect: "none"
        },
        children: [label, " Formatting:"]
      }), inlineButtons.map(({
        label: btnLabel,
        cmd,
        icon
      }) => /*#__PURE__*/_jsx(Tooltip, {
        title: selectionActive ? btnLabel : tooltipFallback,
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(IconButton, {
            size: "small",
            onClick: () => applyInline(cmd),
            children: icon
          })
        })
      }, cmd)), /*#__PURE__*/_jsx(Tooltip, {
        title: selectionActive ? "Text Color" : tooltipFallback,
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(IconButton, {
            ref: textColorBtnRef,
            size: "small",
            onClick: () => {
              const sel = window.getSelection();
              savedRangeRef.current = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
              setTextColorPickerOpen(true);
            },
            children: /*#__PURE__*/_jsxs(Box, {
              sx: {
                position: "relative",
                display: "flex"
              },
              children: [/*#__PURE__*/_jsx(FormatColorTextIcon, {
                fontSize: "small"
              }), /*#__PURE__*/_jsx(Box, {
                sx: {
                  position: "absolute",
                  bottom: -1,
                  left: 1,
                  right: 1,
                  height: 3,
                  bgcolor: textColor,
                  borderRadius: 0.5
                }
              })]
            })
          })
        })
      }), /*#__PURE__*/_jsx(Tooltip, {
        title: selectionActive ? "Highlight" : tooltipFallback,
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(IconButton, {
            ref: highlightBtnRef,
            size: "small",
            onClick: () => {
              const sel = window.getSelection();
              savedRangeRef.current = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
              setHighlightPickerOpen(true);
            },
            children: /*#__PURE__*/_jsxs(Box, {
              sx: {
                position: "relative",
                display: "flex"
              },
              children: [/*#__PURE__*/_jsx(BorderColorIcon, {
                fontSize: "small"
              }), /*#__PURE__*/_jsx(Box, {
                sx: {
                  position: "absolute",
                  bottom: -1,
                  left: 1,
                  right: 1,
                  height: 3,
                  bgcolor: highlight,
                  borderRadius: 0.5
                }
              })]
            })
          })
        })
      }), /*#__PURE__*/_jsx(Tooltip, {
        title: selectionActive ? "Clear Formatting" : tooltipFallback,
        children: /*#__PURE__*/_jsx("span", {
          children: /*#__PURE__*/_jsx(Button, {
            size: "small",
            onClick: resetFormatting,
            sx: {
              fontSize: "0.7rem",
              px: 1,
              py: 0.25,
              minWidth: 0,
              textTransform: "none",
              color: "text.secondary"
            },
            children: "Clear"
          })
        })
      })]
    }), /*#__PURE__*/_jsxs(Box, {
      sx: {
        position: "relative",
        mt: 0.5
      },
      children: [label && /*#__PURE__*/_jsx(Typography, {
        component: "label",
        variant: "caption",
        sx: {
          position: "absolute",
          top: -9,
          left: 10,
          px: 0.5,
          bgcolor: "background.paper",
          color: focused ? "primary.main" : "text.secondary",
          fontSize: "0.75rem",
          lineHeight: "1.4375em",
          zIndex: 1,
          pointerEvents: "none",
          userSelect: "none"
        },
        children: label
      }), /*#__PURE__*/_jsx(Box, {
        id: id,
        ref: editorRef,
        contentEditable: true,
        suppressContentEditableWarning: true,
        onFocus: () => setFocused(true),
        onBlur: () => setFocused(false),
        onInput: () => {
          if (editorRef.current) {
            internalRef.current = true;
            onChange(editorRef.current.innerHTML);
          }
        },
        sx: {
          minHeight,
          border: focused ? "2px solid" : "1px solid",
          borderColor: focused ? "primary.main" : "rgba(0,0,0,0.23)",
          borderRadius: "4px",
          p: "14px 14px",
          outline: "none",
          fontFamily: "inherit",
          fontSize: "1rem",
          lineHeight: 1.5,
          cursor: "text",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          "&:empty::before": {
            content: `"${placeholder.replace(/"/g, '\\"')}"`,
            color: "rgba(0,0,0,0.38)",
            pointerEvents: "none"
          }
        }
      })]
    }), /*#__PURE__*/_jsx(ColorPicker, {
      anchorEl: textColorBtnRef.current,
      open: textColorPickerOpen,
      onClose: () => setTextColorPickerOpen(false),
      title: "Text Color",
      currentColor: textColor,
      onSelect: color => {
        const c = color ?? "#000000";
        setTextColor(c);
        setTextColorPickerOpen(false);
        applyColor("color", c, savedRangeRef.current);
      }
    }), /*#__PURE__*/_jsx(ColorPicker, {
      anchorEl: highlightBtnRef.current,
      open: highlightPickerOpen,
      onClose: () => setHighlightPickerOpen(false),
      title: "Highlight",
      currentColor: highlight,
      onSelect: color => {
        const c = color ?? "#ffff00";
        setHighlight(c);
        setHighlightPickerOpen(false);
        applyColor("backgroundColor", c, savedRangeRef.current);
      }
    })]
  });
}